import React from 'react';
import styled from 'styled-components';
import currencyFormatter from 'currency-formatter';
import { useTranslation } from 'react-i18next';
import { UIOrderProps } from './types';
import { ProductImage } from './index';
import Breakpoints from '../../design-utils/breakpoints';
import { getGidFromIdForResource } from '../../../utils/gid-utils';

interface OrderItemizationProps {
  order: UIOrderProps;
  images: Record<string, string>;
}

const OrderItemization: React.FC<OrderItemizationProps> = ({
  order,
  images = {},
}): JSX.Element => {
  const { t } = useTranslation();
  const { items, totalPrice, subtotalPrice, totalShipping, totalTax } = order;
  const taxAmount = Number(totalTax?.amount) || 0;

  return (
    <ItemizationContainer>
      <ItemizationList>
        {items.map((item) => {
          const {
            variantId,
            productTitle,
            variantTitle,
            quantity,
            itemTotal,
            itemCurrency,
          } = item;
          const imageKey = getGidFromIdForResource(variantId, 'ProductVariant');

          return (
            <ItemRow key={variantId}>
              <ItemDescription>
                <ProductImage src={images[imageKey]} itemQty={quantity} />
                <div>
                  <ProductName>{productTitle}</ProductName>
                  <VariantSubtitle>{variantTitle}</VariantSubtitle>
                </div>
                {variantTitle && (
                  <>
                    <Interpoint>{' • '}</Interpoint>
                    <VariantName>{variantTitle}</VariantName>
                  </>
                )}
              </ItemDescription>
              <div>
                {currencyFormatter.format(itemTotal, {
                  code: itemCurrency,
                })}
              </div>
            </ItemRow>
          );
        })}
      </ItemizationList>
      <Divider />
      <ItemRow>
        <TotalAmount>{t('lastOrder.subtotal')}</TotalAmount>
        <TotalAmount>
          {currencyFormatter.format(subtotalPrice.amount, {
            code: subtotalPrice.currency,
          })}
        </TotalAmount>
      </ItemRow>
      {taxAmount > 0 && (
        <ItemRow>
          <TotalAmount>{t('lastOrder.tax')}</TotalAmount>
          <TotalAmount>
            {currencyFormatter.format(totalTax.amount, {
              code: totalTax.currency,
            })}
          </TotalAmount>
        </ItemRow>
      )}
      <ItemRow>
        <TotalAmount>{t('lastOrder.shipping')}</TotalAmount>
        <TotalAmount>
          {currencyFormatter.format(totalShipping.amount, {
            code: totalShipping.currency,
          })}
        </TotalAmount>
      </ItemRow>
      <ItemRow>
        <TotalAmount>{t('lastOrder.total')}</TotalAmount>
        <TotalAmount>
          {currencyFormatter.format(totalPrice.amount, {
            code: totalPrice.currency,
          })}
        </TotalAmount>
      </ItemRow>
    </ItemizationContainer>
  );
};

const ItemizationContainer = styled.div.attrs({
  className: 'awt-order-itemization',
})`
  margin-top: 16px;
`;
const ItemizationList = styled.div.attrs({
  className: 'awt-order-itemization-list',
})`
  overflow: hidden;
  overflow-y: scroll;
  max-height: 195px;

  &::-webkit-scrollbar {
    background: transparent;
    border: none;
    height: 1 rem;
    width: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 10px;
    border: none;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
  }
  & {
    scrollbar-width: thin;
    scrollbar-color: #c1c1c1 transparent;
  }
`;

const ItemRow = styled.div.attrs({
  className: 'awt-order-itemization-row',
})`
  display: flex;
  justify-content: space-between;
  margin-bottom: 16px;
  margin-top: 4px;
  padding-right: 20px;
`;
const ItemDescription = styled.div.attrs({
  className: 'awt-order-itemization-description',
})`
  display: flex;
  align-items: center;
`;
const ProductName = styled.div.attrs({
  className: 'awt-order-itemization-row-product',
})`
  margin-left: 16px;
  font-size: 18px;
  font-weight: 600;
`;
const VariantSubtitle = styled.div.attrs({
  className: 'awt-order-itemization-row-variant-sub',
})`
  display: none;

  @media (max-width: ${Breakpoints.sm}) {
    font-size: 16px;
    color: #637381;
    margin-left: 16px;
    display: block;
  }
`;
const VariantName = styled.div.attrs({
  className: 'awt-order-itemization-row-variant',
})`
  font-size: 16px;
  color: #637381;

  @media (max-width: ${Breakpoints.sm}) {
    display: none;
  }
`;
const TotalAmount = styled.div.attrs({
  className: 'awt-order-itemization-amount-item',
})`
  font-size: 16px;
  font-weight: 700;
`;
const Interpoint = styled.div.attrs({
  className: 'awt-order-itemization-interpoint',
})`
  color: #d9d9d9;
  font-weight: 600;
  margin: 0 8px;

  @media (max-width: ${Breakpoints.sm}) {
    display: none;
  }
`;
const Divider = styled.div.attrs({
  className: 'awt-order-itemization-divider',
})`
  border-top: 1px solid #dfe3e8;
  margin-top: 16px;
  margin-bottom: 16px;
`;

export default OrderItemization;
